/**
 * Whether a new call may start: the one check behind every place that refuses
 * a call with "Finish your current call first" or "Already in a call" (VRS
 * Home, Recents, the call-back review).
 *
 * Robert, 2026-10-12 13:44 EDT, MalkaVRS: a call to Nataly got no answer, and
 * about 10 s later the next call was refused. Telemetry showed no call request
 * reached the server, so the app refused it. The app had ended the unanswered
 * call itself (the ring's expiry), but the server keeps an unanswered 1:1 row
 * open until the caller's cancel is processed (so the callee can still join),
 * and Home, loading at that moment, asked the server for an open call and
 * adopted that one. Each place read its own mix of the stored call, the queue
 * match and a pending request, and nothing re-checked a doubtful one.
 *
 * The rules:
 * - A call this device ended (noteCallEndedHere, from the call exit) is never
 *   a reason to refuse, and Home never adopts it from the server.
 * - Fast, no network: a conference joined or joining, or a request out, is a
 *   real call. So is a stored call that started less than STORED_CALL_GRACE_MS
 *   ago (the server's record of a new call can lag; the middleware's
 *   reconcile uses the same grace).
 * - A stored call or a queue match with no conference is checked with the
 *   server (GET /api/client/active-call), at most CALL_BUSY_CONFIRM_TIMEOUT_MS.
 *   The same call, or another live one: refuse. None: the stored call is
 *   stale (an exit preserved it for a rejoin, and the call ended meanwhile):
 *   it is cleared, and the call may start.
 * - The server cannot be reached: refuse, saying so. A new call needs the same
 *   server, so allowing it gains nothing, and it could start a second call
 *   beside a live one.
 * - Every refusal logs which source said busy (call_busy).
 *
 * Robert, 2026-10-13: "why can't we even make a second call again? Shouldn't
 * that just put the first call on hold? And if it's not even happening". A
 * refusal now offers the way out when there is one (presentCallBusyChoice;
 * the queue middleware's START_CALL_AFTER_BUSY does it):
 * - In a direct or group call: "Hold and call". The call goes on hold and the
 *   new call goes out (the in-call bar swaps back, ends or merges it).
 * - In a call with an interpreter (VRS, VRI), or with a call already on hold
 *   (one held call at a time): "End and call". A call with an interpreter is
 *   never held (call-management/holdPolicy.ts, the server's
 *   lib/call-hold-policy.js).
 * - Not in a call, but the server has one open that this device is not in (it
 *   crashed or was force-quit in it, or the call is on another device): "An
 *   earlier call is still open" with "End it and call". It ends that call
 *   for this person, as their own hang-up would, only on that tap.
 * - The call this device holds is not a reason to refuse.
 * The offer and the tapped action are logged (call_busy, call_busy_action).
 *
 * The only runtime import is the pure hold rule: each screen passes the API,
 * storage and logger it already uses, so the screens' sandboxed test
 * harnesses stay small.
 */
import { canHoldStoredCall } from '../call-management/holdPolicy';
/** What a refusal says, and its button, for each offer. No other text (no explanatory copy). */
export const CALL_BUSY_CHOICES = {
    end_and_call: { confirm: 'End and call', title: 'Already in a call' },
    end_open_and_call: { confirm: 'End it and call', title: 'An earlier call is still open' },
    hold_and_call: { confirm: 'Hold and call', title: 'Already in a call' }
};
/** Shown when the server could not be asked whether a call is in progress. */
export const CALL_BUSY_UNCHECKED_MESSAGE = 'Could not check for a call in progress. Try again.';
/** Longest wait for the server's answer before a call is refused. */
export const CALL_BUSY_CONFIRM_TIMEOUT_MS = 2500;
/** A stored call younger than this is real without asking (middleware ACTIVE_CALL_EVICTION_GRACE_MS). */
export const STORED_CALL_GRACE_MS = 20000;
const ENDED_HERE_LIMIT = 50;
const endedHere = new Set();
/**
 * Records a call this device ended (the call exit). Its id and room are never
 * again a reason to refuse a call or to adopt it from the server.
 *
 * @param {Object} [call] - The ended call's id and room.
 * @returns {void}
 */
export function noteCallEndedHere(call) {
    for (const key of [call?.callId, call?.roomName]) {
        if (key) {
            endedHere.delete(key);
            endedHere.add(key);
        }
    }
    while (endedHere.size > ENDED_HERE_LIMIT) {
        endedHere.delete(endedHere.values().next().value);
    }
}
/**
 * Whether this device ended the call with this id or room.
 *
 * @param {string} [key] - A call id or room name.
 * @returns {boolean}
 */
export function wasCallEndedHere(key) {
    return Boolean(key && endedHere.has(key));
}
/**
 * For tests: forgets every ended call.
 *
 * @returns {void}
 */
export function forgetCallsEndedHere() {
    endedHere.clear();
}
/**
 * The start time of a stored call: native stores a number, web an ISO string.
 *
 * @param {unknown} value - The stored startedAt.
 * @returns {number} Milliseconds, or NaN.
 */
function startedAtMs(value) {
    return typeof value === 'number' ? value : Date.parse(String(value ?? ''));
}
/**
 * Runs `work` and rejects with Error('timeout') after `timeoutMs`, aborting it.
 *
 * @param {Function} work - Gets an AbortSignal.
 * @param {number} timeoutMs - The limit.
 * @returns {Promise}
 */
function withTimeout(work, timeoutMs) {
    const controller = new AbortController();
    let timer;
    return Promise.race([
        work(controller.signal),
        new Promise((_resolve, reject) => {
            timer = setTimeout(() => {
                controller.abort();
                reject(new Error('timeout'));
            }, timeoutMs);
        })
    ]).finally(() => {
        if (timer) {
            clearTimeout(timer);
        }
    });
}
/**
 * Whether a new call may start now. See the module comment for the rules.
 *
 * @param {ICallBusyLocalState} state - What this device knows.
 * @param {ICallBusyDeps} deps - The server check, the stored-call clear, the logger.
 * @returns {Promise<CallBusyResult>}
 */
export async function checkCallBusy(state, deps) {
    const now = deps.now ?? Date.now;
    const refuse = (source, checked, details = {}, way = {}) => {
        deps.log('call_busy', {
            checked,
            source,
            ...way.offer ? { offer: way.offer } : {},
            ...way.openCase ? { case: way.openCase } : {},
            ...details
        });
        return {
            busy: true,
            checked,
            source,
            ...way.offer ? { offer: way.offer } : {},
            ...way.openCall ? { openCall: way.openCall } : {},
            ...way.openCase ? { openCase: way.openCase } : {}
        };
    };
    if (state.inConference) {
        return refuse('in_conference', true, {}, { offer: inCallOffer(state.currentCall ?? state.storedCall, state.heldCallId) });
    }
    if (state.isRequestPending) {
        return refuse('request_pending', true);
    }
    const stored = state.storedCall?.roomName && !wasCallEndedHere(state.storedCall.callId)
        && !wasCallEndedHere(state.storedCall.roomName) ? state.storedCall : null;
    const match = state.matchRoomName && !wasCallEndedHere(state.matchRoomName) ? state.matchRoomName : undefined;
    if (!stored && !match) {
        if (state.storedCall?.roomName || state.matchRoomName) {
            deps.log('call_busy_ignored', { reason: 'ended_here' });
        }
        return { busy: false };
    }
    const source = stored ? 'stored_call' : 'queue_match';
    const age = stored ? now() - startedAtMs(stored.startedAt) : Number.NaN;
    if (stored && Number.isFinite(age) && age >= 0 && age < STORED_CALL_GRACE_MS) {
        return refuse(source, true, { reason: 'just_started' });
    }
    let answer;
    try {
        answer = await withTimeout(deps.fetchActiveCall, deps.timeoutMs ?? CALL_BUSY_CONFIRM_TIMEOUT_MS);
    }
    catch (error) {
        const reason = error instanceof Error && error.message === 'timeout' ? 'timeout' : 'unreachable';
        return refuse(source, false, { reason });
    }
    if (answer?.error) {
        return refuse(source, false, { reason: 'server_error' });
    }
    const serverCall = answer?.data?.call;
    const heldHere = Boolean(serverCall?.id && state.heldCallId === serverCall.id);
    if (serverCall?.id && !heldHere && !wasCallEndedHere(serverCall.id) && !wasCallEndedHere(serverCall.room_name)) {
        const same = Boolean((stored?.callId && serverCall.id === stored.callId)
            || (serverCall.room_name && (serverCall.room_name === stored?.roomName || serverCall.room_name === match)));
        let openCase = same ? 'left_open' : 'other_call';
        if (state.elsewhereCallId && state.elsewhereCallId === serverCall.id) {
            openCase = 'other_device';
        }
        // Open on the server, and this device is in no conference: it ended
        // here without a hang-up (a crash, a force-quit), or it is another
        // device's call. Offered to end, never ended without the tap.
        return refuse('server_call', true, {
            local: source,
            same
        }, {
            offer: 'end_open_and_call',
            openCall: {
                callId: serverCall.id,
                ...serverCall.room_name ? { roomName: serverCall.room_name } : {}
            },
            openCase
        });
    }
    // The server shows no call (or only one this device ended, or holds): the
    // local record outlived its call.
    if (heldHere) {
        deps.log('call_busy_ignored', { reason: 'held_here' });
    }
    if (stored) {
        deps.clearStoredCall();
    }
    deps.log('call_busy_stale_cleared', { source });
    return { busy: false };
}
/**
 * The offer while this device is in a conference: Hold and call for a call
 * that may go on hold (a direct or group call, holdPolicy.ts) when no other
 * call is held here; End and call for any other call; none when the
 * conference has no call record (nothing to hold or end).
 *
 * @param {ICallBusyRecord} [current] - The conference's call record.
 * @param {string} [heldCallId] - The call this device already holds.
 * @returns {CallBusyOffer|undefined}
 */
function inCallOffer(current, heldCallId) {
    if (!current?.callId) {
        return undefined;
    }
    return canHoldStoredCall(current) && (!heldCallId || heldCallId === current.callId)
        ? 'hold_and_call' : 'end_and_call';
}
/**
 * Says that a call cannot start now: with the offer and its one button when
 * there is a way out, else the screen's refusal. Logs the tapped action.
 *
 * @param {CallBusyResult} busy - A busy result of checkCallBusy.
 * @param {ICallBusyChoiceDeps} deps - The alert, logger and next step.
 * @returns {void}
 */
export function presentCallBusyChoice(busy, deps) {
    const offer = busy.offer;
    if (!offer) {
        deps.refuse(busy.checked ? deps.refusal : CALL_BUSY_UNCHECKED_MESSAGE);
        return;
    }
    const choice = CALL_BUSY_CHOICES[offer];
    const details = {
        offer,
        source: busy.source,
        ...busy.openCase ? { case: busy.openCase } : {}
    };
    deps.alert(choice.title, undefined, [
        {
            onPress: () => deps.log('call_busy_action', { action: 'cancel', ...details }),
            style: 'cancel',
            text: 'Cancel'
        },
        {
            onPress: () => {
                deps.log('call_busy_action', { action: offer, ...details });
                deps.proceed();
            },
            style: offer === 'hold_and_call' ? 'default' : 'destructive',
            text: choice.confirm
        }
    ]);
}
